/**
 * الأوامر المحلية — تقابل دوال PostgreSQL في وضع Supabase.
 *
 * كل أمر:
 *   1. يتأكد من الهوية والعضوية والشركة،
 *   2. يتحقق من الصلاحية عبر محرك الصلاحيات،
 *   3. ينفّذ التغيير داخل معاملة واحدة (IndexedDB transaction) تشمل
 *      سجل الحالة والإشعار والتدقيق،
 *   4. يتحقق من رقم النسخة (version) لمنع الكتابة فوق تعديل أحدث.
 *
 * لا يُسمح للواجهة بتغيير حالة مهمة بتحديث مباشر؛ هذه الأوامر هي المسار الوحيد.
 * ملفات المرفقات (Blob) تُكتب خارج المعاملة لأن IndexedDB لا يدعم معاملة
 * تشمل التخزين والبيانات معًا؛ لذلك نُنظّف الملف يدويًا عند فشل التسجيل.
 */

import {
  canApproveTask,
  canAssignTask,
  canAssignToMembership,
  canCompleteTask,
  canDeleteTask,
  canEditTask,
  type TaskAccessSubject,
} from '@/domain/access';
import { AUDIT_ACTIONS, redactForAudit, severityForAction } from '@/domain/audit';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { DELEGABLE_PLATFORM_PERMISSIONS, type PermissionKey } from '@/domain/permissions';
import {
  addDaysLocal,
  planOccurrences,
  validateRule,
} from '@/domain/recurrence';
import { approvalPatch, completionPatch, evaluateCompletion } from '@/domain/task-lifecycle';
import {
  DEFAULT_COMPLETED_TASK_RETENTION,
  DEFAULT_RETENTION_POLICY,
  DEFAULT_UPLOAD_POLICY,
  SETTING_KEYS,
  retentionExpiry,
  resolveSetting,
  type CompletedTaskRetentionPolicy,
  type RetentionPolicy,
  type UploadPolicy,
} from '@/domain/settings';
import type {
  Attachment,
  AuditLog,
  Company,
  Membership,
  MembershipRole,
  NotificationType,
  PlatformOwner,
  Profile,
  Role,
  SettingRecord,
  Task,
  TaskAssignee,
  TaskStatusHistory,
  TaskStep,
  TaskTemplate,
  ThemeTokens,
} from '@/domain/types';
import type {
  ApplyRetentionInput,
  BootstrapOwnerInput,
  BootstrapStatus,
  CompleteTaskInput,
  CreateCompanyInput,
  AddMembershipCommandInput,
  CreateTaskCommandInput,
  UpdateTaskCommandInput,
  GenerateOccurrencesInput,
  GenerateOccurrencesResult,
  GatewayCommands,
  OrgArchiveQuery,
  OrgLifecycleInput,
  ArchivedOrgRecord,
  PlatformBranding,
  PlatformActivityEvent,
  PlatformActivityRetentionSettings,
  PurgeCompletedTasksInput,
  PurgeCompletedTasksResult,
  Repository,
  Row,
  SignInResult,
  TableName,
  UploadAttachmentInput,
  RoleDeletionImpact,
} from '../types';
import { DEFAULT_PLATFORM_BRANDING } from '../types';
import { getDeviceSetting, setDeviceSetting } from '@/services/settings-service';
import { buildStoragePath, sha256Hex, type BlobStore } from '../blob-store';
import type { PolicyContext } from '../policy';
import {
  createLocalAccount,
  findAccountByEmail,
  hashSecret,
  verifySecret,
  type LocalAuthGateway,
} from './auth';
import { getLocalDb, TABLE_STORES, type TaskflowDatabase } from './db';
import { listOrgArchiveLocal, manageOrgEntityLocal } from './org-lifecycle';
import { seedCompanyStructure } from './company-setup';
import {
  readPlatformOperationsSettings,
  getPlatformOperationsSettingsLocal,
  setPlatformOperationsSettingsLocal,
  saveSubscriptionPlanLocal,
  saveCompanySubscriptionLocal,
  recordSubscriptionPaymentLocal,
  updateSubscriptionPaymentLocal,
  createCompanyInviteLocal,
  acceptCompanyInviteLocal,
  selfRegisterCompanyLocal,
} from './commercial';

export interface LocalCore {
  policy(): PolicyContext;
  table<T extends TableName>(name: T): Repository<Row<T>>;
  rawGet<T extends TableName>(name: T, id: string): Promise<Row<T> | null>;
  rawFind<T extends TableName>(name: T, field: string, value: unknown): Promise<Row<T>[]>;
  rawAll<T extends TableName>(name: T): Promise<Row<T>[]>;
  rawPut<T extends TableName>(name: T, row: Row<T>): Promise<void>;
  rawDelete<T extends TableName>(name: T, id: string): Promise<void>;
  blob: BlobStore;
  audit(input: {
    companyId: string | null;
    action: string;
    entityType: string;
    entityId: string | null;
    before?: unknown;
    after?: unknown;
  }): Promise<void>;
  now(): Date;
  refresh(): Promise<void>;
  auth: LocalAuthGateway;
}

const LOCAL_ACTIVITY_RETENTION_KEY = 'platform.activity-retention';

function localActivityRetention(): PlatformActivityRetentionSettings {
  return getDeviceSetting<PlatformActivityRetentionSettings>(LOCAL_ACTIVITY_RETENTION_KEY, {
    enabled: false,
    retentionDays: null,
    updatedAt: null,
    updatedBy: null,
  });
}

async function purgeExpiredLocalActivity(core: LocalCore): Promise<number> {
  const setting = localActivityRetention();
  if (!setting.enabled || !setting.retentionDays) return 0;
  const cutoff = core.now().getTime() - setting.retentionDays * 24 * 60 * 60 * 1000;
  const expired = (await core.rawAll('audit_logs')).filter((event) => Date.parse(event.createdAt) < cutoff);
  for (const event of expired) await core.rawDelete('audit_logs', event.id);
  if (expired.length) await core.refresh();
  return expired.length;
}

/* ============================== حرّاس أساسيون ============================== */

function requireUser(core: LocalCore): string {
  const userId = core.policy().userId;
  if (!userId) throw AppError.unauthenticated();
  return userId;
}

function requireCompany(core: LocalCore): string {
  const ctx = core.policy();
  if (!ctx.userId) throw AppError.unauthenticated();
  if (!ctx.companyId) throw AppError.forbidden('لا توجد عضوية نشطة في أي منشأة');
  return ctx.companyId;
}

function requirePermission(core: LocalCore, permission: PermissionKey): void {
  if (!core.policy().permissions.has(permission)) {
    throw AppError.forbidden('لا تملك الصلاحية المطلوبة لهذه العملية');
  }
}

function requireCompanyRoleManager(core: LocalCore, companyId: string): void {
  const ctx = core.policy();
  if (!ctx.userId) throw AppError.unauthenticated();
  if (!ctx.companyId || ctx.companyId !== companyId) {
    throw AppError.forbidden('لا يمكن إدارة أدوار منشأة أخرى');
  }
  if (!ctx.permissions.has('role.manage') && ctx.access.membership?.membership.isCompanyOwner !== true) {
    throw AppError.forbidden('إدارة الأدوار متاحة لمالك المنشأة أو للمفوّضين بهذه الصلاحية فقط');
  }
}

function requireMembership(core: LocalCore, membershipId: string | null): void {
  if (!membershipId) throw AppError.forbidden('وضع دعم المنصة غير متاح في التخزين المحلي');
  if (core.policy().membershipId !== membershipId) {
    throw AppError.forbidden('لا يمكنك تنفيذ الإجراء باسم عضوية أخرى');
  }
}

async function loadTask(core: LocalCore, taskId: string): Promise<Task> {
  const companyId = requireCompany(core);
  const task = await core.rawGet('tasks', taskId);
  if (!task || task.deletedAt || task.companyId !== companyId) {
    throw AppError.notFound('المهمة غير موجودة');
  }
  return task;
}

async function subjectFor(core: LocalCore, task: Task): Promise<TaskAccessSubject> {
  const assignees = (await core.rawFind('task_assignees', 'taskId', task.id)).filter((a) => !a.unassignedAt);
  return {
    task,
    assigneeMembershipIds: assignees.map((a) => a.membershipId),
    completerMembershipIds: assignees.filter((a) => a.state === 'completed').map((a) => a.membershipId),
  };
}

function scopeOf(core: LocalCore) {
  const scope = core.policy().scope;
  if (!scope) throw AppError.forbidden('لا يوجد نطاق وصول نشط');
  return scope;
}

/* ============================== معاملات آمنة ============================== */

/**
 * كل جداول التطبيق في معاملة واحدة. الأوامر تتشارك هذا المعاملة لتسجيل
 * التدقيق والإشعارات والعلاقات المرتبطة؛ حصر القائمة سابقًا تسبب في فشل
 * أوامر العضويات لأن جداولها لم تكن ضمن المعاملة.
 */
const TX_STORES = TABLE_STORES;

type AnyStore = {
  get(key: string): Promise<unknown>;
  getAll(): Promise<unknown[]>;
  put(value: unknown): Promise<unknown>;
  delete(key: string): Promise<unknown>;
};

function storeOf(tx: { objectStore(name: string): unknown }, name: string): AnyStore {
  return tx.objectStore(name) as AnyStore;
}

async function withTx<T>(
  _db: TaskflowDatabase,
  work: (tx: {
    objectStore(name: string): unknown;
    done: Promise<void>;
  }) => Promise<T>,
): Promise<T> {
  const db = await getLocalDb();
  const tx = db.transaction(TX_STORES, 'readwrite');
  const result = await work(tx as unknown as { objectStore(name: string): unknown; done: Promise<void> });
  await tx.done;
  return result;
}

function auditRow(
  userId: string | null,
  membershipId: string | null,
  input: {
    companyId: string | null;
    action: string;
    entityType: string;
    entityId: string | null;
    before?: unknown;
    after?: unknown;
  },
  now: string,
): AuditLog {
  return {
    id: newId(),
    companyId: input.companyId,
    actorUserId: userId,
    actorMembershipId: membershipId,
    action: input.action,
    entityType: input.entityType,
    entityId: input.entityId,
    before: redactForAudit(input.before ?? null),
    after: redactForAudit(input.after ?? null),
    severity: severityForAction(input.action),
    requestId: null,
    createdAt: now,
  };
}

async function recipientUserId(core: LocalCore, membershipId: string | null): Promise<string | null> {
  if (!membershipId) return null;
  const membership = await core.rawGet('memberships', membershipId);
  if (!membership || membership.status !== 'active') return null;
  return membership.userId;
}

function notificationRow(input: {
  companyId: string;
  recipientUserId: string;
  type: NotificationType;
  title: string;
  body: string | null;
  taskId: string | null;
  now: string;
}) {
  return {
    id: newId(),
    companyId: input.companyId,
    recipientUserId: input.recipientUserId,
    type: input.type,
    title: input.title,
    body: input.body,
    taskId: input.taskId,
    payload: {},
    readAt: null,
    createdAt: input.now,
  };
}

/* ================================ الأوامر ================================ */

export function createCommands(core: LocalCore): GatewayCommands {
  async function assertAssigneeTargets(membershipIds: string[]): Promise<Membership[]> {
    const ctx = core.policy();
    const companyId = requireCompany(core);
    const targets: Membership[] = [];
    for (const membershipId of membershipIds) {
      const membership = await core.rawGet('memberships', membershipId);
      if (!membership || membership.companyId !== companyId || membership.status !== 'active') {
        throw AppError.validation('المستخدم المسند إليه غير نشط أو خارج المنشأة');
      }
      if (membership.id !== ctx.membershipId) {
        const decision = canAssignToMembership({
          ctx: ctx.access,
          scope: scopeOf(core),
          targetMembership: { id: membership.id, companyId: membership.companyId },
        });
        if (!decision.allowed) throw AppError.forbidden(decision.reason);
      }
      targets.push(membership);
    }
    return targets;
  }

  function unique(values: string[]): string[] {
    return [...new Set(values)];
  }

  return {
    async createTask(input: CreateTaskCommandInput): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.actorMembershipId);
      requirePermission(core, 'task.create');

      const title = input.task.title.trim();
      if (title.length < 2 || title.length > 200) {
        throw AppError.validation('عنوان المهمة يجب أن يكون بين حرفين و200 حرف');
      }
      const start = new Date(input.task.startAt);
      if (Number.isNaN(start.getTime())) throw AppError.validation('وقت البدء غير صالح');
      const due = input.task.dueAt ? new Date(input.task.dueAt) : null;
      if (due && Number.isNaN(due.getTime())) throw AppError.validation('الموعد النهائي غير صالح');
      if (due && due.getTime() < start.getTime()) {
        throw AppError.validation('الموعد النهائي يجب أن يكون بعد وقت البدء');
      }

      const category = await core.rawGet('task_categories', input.task.categoryId);
      if (!category || category.companyId !== companyId) {
        throw AppError.validation('فئة المهمة غير صالحة');
      }
      if (input.task.branchId) {
        const branch = await core.rawGet('branches', input.task.branchId);
        if (!branch || branch.companyId !== companyId) throw AppError.validation('الفرع غير صالح');
      }
      if (input.task.brandId) {
        const brand = await core.rawGet('brands', input.task.brandId);
        if (!brand || brand.companyId !== companyId) throw AppError.validation('العلامة غير صالحة');
      }
      if (input.task.approverMembershipId) {
        const approver = await core.rawGet('memberships', input.task.approverMembershipId);
        if (!approver || approver.companyId !== companyId || approver.status !== 'active') {
          throw AppError.validation('المعتمد غير صالح');
        }
        if (!input.task.requiresApproval) {
          throw AppError.validation('لا يمكن تحديد معتمد لمهمة لا تتطلب اعتمادًا');
        }
      }

      const permissions = core.policy().permissions;
      const canShare = permissions.has('task.share');
      let assigneeIds = unique(input.task.assigneeMembershipIds);
      // الإسناد بنطاق: يُحل إلى عضويات نشطة في الفرع (أو الفروع التابعة للعلامة).
      if (input.task.assignmentMode === 'scope' && assigneeIds.length === 0) {
        const companyMembers = (await core.rawFind('memberships', 'companyId', companyId)).filter(
          (m) => m.status === 'active',
        );
        let branchIds = input.task.branchId ? [input.task.branchId] : [];
        if (branchIds.length === 0 && input.task.brandId) {
          const branches = await core.rawFind('branches', 'companyId', companyId);
          branchIds = branches
            .filter((b) => b.brandId === input.task.brandId && !b.deletedAt)
            .map((b) => b.id);
        }
        if (branchIds.length > 0) {
          assigneeIds = companyMembers
            .filter((m) => m.primaryBranchId && branchIds.includes(m.primaryBranchId))
            .map((m) => m.id);
        }
        if (assigneeIds.length === 0) assigneeIds = [input.actorMembershipId!];
      }
      if (input.task.isPersonal && assigneeIds.length === 0) assigneeIds = [input.actorMembershipId!];
      if (input.task.isPersonal) {
        if (input.task.visibility !== 'private' && !canShare) {
          throw AppError.forbidden('مشاركة المهمة الشخصية تتطلب صلاحية مشاركة المهام');
        }
        if (
          input.task.visibility === 'private' &&
          !canShare &&
          assigneeIds.some((id) => id !== input.actorMembershipId)
        ) {
          throw AppError.forbidden('لا يمكن إسناد مهمة شخصية خاصة لشخص آخر دون صلاحية المشاركة');
        }
      } else if (assigneeIds.length === 0 && input.task.assignmentMode !== 'scope') {
        throw AppError.validation('اختر مسؤولًا واحدًا على الأقل');
      }

      const targets = await assertAssigneeTargets(assigneeIds);
      const at = core.now().toISOString();
      const task: Task = {
        id: newId(),
        companyId,
        brandId: input.task.brandId,
        branchId: input.task.branchId,
        categoryId: input.task.categoryId,
        templateId: null,
        occurrenceKey: `manual:${newId()}`,
        title,
        description: input.task.description?.trim() || null,
        instructions: input.task.instructions?.trim() || null,
        taskType: input.task.taskType,
        priority: input.task.priority,
        status: start.getTime() > core.now().getTime() ? 'scheduled' : 'pending',
        approvalState: 'not_required',
        assignmentMode:
          input.task.assignmentMode === 'scope'
            ? 'scope'
            : assigneeIds.length > 1
              ? 'multi'
              : 'single',
        completionRule: input.task.completionRule,
        requiresProof: input.task.requiresProof,
        requiresApproval: input.task.requiresApproval,
        approverMembershipId: input.task.approverMembershipId,
        createdByMembershipId: input.actorMembershipId,
        isPersonal: input.task.isPersonal,
        visibility: input.task.visibility,
        assigneeScope: {
          branchIds: input.task.branchId ? [input.task.branchId] : [],
          brandIds: input.task.brandId ? [input.task.brandId] : [],
          membershipIds: assigneeIds,
          includeSupervised: input.task.assignmentMode === 'scope',
        },
        startAt: input.task.startAt,
        dueAt: input.task.dueAt,
        completedAt: null,
        approvedAt: null,
        approvedByMembershipId: null,
        returnedAt: null,
        returnedByMembershipId: null,
        returnedReason: null,
        cancelledAt: null,
        cancelledByMembershipId: null,
        localDate: input.task.localDate,
        timeZone: input.task.timeZone,
        version: 1,
        createdAt: at,
        updatedAt: at,
        deletedAt: null,
      };

      const assigneeRows: TaskAssignee[] = targets.map((membership) => ({
        id: newId(),
        taskId: task.id,
        membershipId: membership.id,
        state: 'pending',
        assignedAt: at,
        assignedByMembershipId: input.actorMembershipId,
        unassignedAt: null,
        unassignedReason: null,
        completedAt: null,
        completedByMembershipId: null,
        proofNote: null,
      }));

      const steps: TaskStep[] = (input.steps ?? [])
        .map((step) => step.trim())
        .filter((step) => step.length > 0)
        .slice(0, 100)
        .map((stepTitle, index) => ({
          id: newId(),
          taskId: task.id,
          title: stepTitle,
          sortOrder: index,
          isDone: false,
          doneByMembershipId: null,
          doneAt: null,
        }));

      const db = await getLocalDb();
      return withTx(db, async (tx) => {
        await storeOf(tx, 'tasks').put(task);
        for (const assignee of assigneeRows) await storeOf(tx, 'task_assignees').put(assignee);
        for (const step of steps) await storeOf(tx, 'task_steps').put(step);
        for (const target of targets) {
          if (target.id === input.actorMembershipId) continue;
          await storeOf(tx, 'notifications').put(
            notificationRow({
              companyId,
              recipientUserId: target.userId,
              type: 'task.assigned',
              title: 'أُسندت إليك مهمة جديدة',
              body: task.title,
              taskId: task.id,
              now: at,
            }),
          );
        }
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskCreated,
              entityType: 'task',
              entityId: task.id,
              after: {
                title: task.title,
                assignees: assigneeIds,
                isPersonal: task.isPersonal,
                requiresApproval: task.requiresApproval,
              },
            },
            at,
          ),
        );
        return task;
      });
    },

    async updateTask(input: UpdateTaskCommandInput): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.actorMembershipId);
      const task = await loadTask(core, input.taskId);
      const subject = await subjectFor(core, task);
      const editable = canEditTask({
        ctx: core.policy().access,
        scope: scopeOf(core),
        subject,
      });
      if (!editable.allowed) throw AppError.forbidden(editable.reason);
      if (task.status === 'completed' || task.status === 'cancelled') {
        throw AppError.conflict('لا يمكن تعديل مهمة مكتملة أو ملغاة قبل إعادة فتحها');
      }

      const patch: Partial<Task> = {};
      if (input.patch.title !== undefined) {
        const title = input.patch.title.trim();
        if (title.length < 2 || title.length > 200) {
          throw AppError.validation('عنوان المهمة يجب أن يكون بين حرفين و200 حرف');
        }
        patch.title = title;
      }
      if (input.patch.description !== undefined) patch.description = input.patch.description?.trim() || null;
      if (input.patch.instructions !== undefined) patch.instructions = input.patch.instructions?.trim() || null;
      if (input.patch.taskType !== undefined) patch.taskType = input.patch.taskType;
      if (input.patch.priority !== undefined) patch.priority = input.patch.priority;
      if (input.patch.completionRule !== undefined) patch.completionRule = input.patch.completionRule;
      if (input.patch.requiresProof !== undefined) patch.requiresProof = input.patch.requiresProof;
      if (input.patch.requiresApproval !== undefined) patch.requiresApproval = input.patch.requiresApproval;
      if (input.patch.approverMembershipId !== undefined) {
        if (input.patch.approverMembershipId) {
          const approver = await core.rawGet('memberships', input.patch.approverMembershipId);
          if (!approver || approver.companyId !== companyId || approver.status !== 'active') {
            throw AppError.validation('المعتمد غير صالح');
          }
        }
        patch.approverMembershipId = input.patch.approverMembershipId;
      }
      if (input.patch.categoryId !== undefined) {
        const category = await core.rawGet('task_categories', input.patch.categoryId);
        if (!category || category.companyId !== companyId) throw AppError.validation('فئة المهمة غير صالحة');
        patch.categoryId = input.patch.categoryId;
      }
      if (input.patch.branchId !== undefined) {
        if (input.patch.branchId) {
          const branch = await core.rawGet('branches', input.patch.branchId);
          if (!branch || branch.companyId !== companyId) throw AppError.validation('الفرع غير صالح');
        }
        patch.branchId = input.patch.branchId;
      }
      if (input.patch.brandId !== undefined) {
        if (input.patch.brandId) {
          const brand = await core.rawGet('brands', input.patch.brandId);
          if (!brand || brand.companyId !== companyId) throw AppError.validation('العلامة غير صالحة');
        }
        patch.brandId = input.patch.brandId;
      }
      if (input.patch.visibility !== undefined) {
        if (task.isPersonal && input.patch.visibility !== 'private' && !core.policy().permissions.has('task.share')) {
          throw AppError.forbidden('توسيع رؤية مهمة شخصية يتطلب صلاحية المشاركة');
        }
        patch.visibility = input.patch.visibility;
      }
      if (input.patch.startAt !== undefined) {
        const start = new Date(input.patch.startAt);
        if (Number.isNaN(start.getTime())) throw AppError.validation('وقت البدء غير صالح');
        patch.startAt = input.patch.startAt;
      }
      if (input.patch.dueAt !== undefined) {
        if (input.patch.dueAt) {
          const due = new Date(input.patch.dueAt);
          if (Number.isNaN(due.getTime())) throw AppError.validation('الموعد النهائي غير صالح');
        }
        patch.dueAt = input.patch.dueAt;
      }
      const effectiveStart = new Date(patch.startAt ?? task.startAt);
      const effectiveDue = patch.dueAt !== undefined ? (patch.dueAt ? new Date(patch.dueAt) : null) : task.dueAt ? new Date(task.dueAt) : null;
      if (effectiveDue && effectiveDue.getTime() < effectiveStart.getTime()) {
        throw AppError.validation('الموعد النهائي يجب أن يكون بعد وقت البدء');
      }
      if (patch.requiresApproval === false) {
        patch.approverMembershipId = null;
      }
      if (patch.requiresApproval === true && input.patch.approverMembershipId === null) {
        patch.approverMembershipId = null;
      }
      if (input.patch.localDate !== undefined) patch.localDate = input.patch.localDate;

      const at = core.now().toISOString();
      const changedFields = Object.keys(patch);
      if (changedFields.length === 0) return task;
      const db = await getLocalDb();
      return withTx(db, async (tx) => {
        const tasks = storeOf(tx, 'tasks');
        const current = (await tasks.get(task.id)) as Task | undefined;
        if (!current || current.version !== task.version) {
          throw AppError.conflict('تم تعديل المهمة من جهة أخرى. أعد التحميل وحاول مرة أخرى.');
        }
        const next: Task = {
          ...current,
          ...patch,
          version: current.version + 1,
          updatedAt: at,
        };
        await tasks.put(next);
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskUpdated,
              entityType: 'task',
              entityId: task.id,
              before: Object.fromEntries(changedFields.map((field) => [field, (current as unknown as Record<string, unknown>)[field]])),
              after: patch,
            },
            at,
          ),
        );
        return next;
      });
    },

    async addMembership(input: AddMembershipCommandInput): Promise<Membership> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.actorMembershipId);
      requirePermission(core, 'membership.manage');
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن إدارة عضويات منشأة أخرى');

      const email = input.email.trim().toLowerCase();
      let account = await findAccountByEmail(email);
      if (!account) {
        if (!input.password) {
          throw AppError.validation(
            'لا يوجد حساب بهذا البريد. اطلب من المستخدم إنشاء حساب أولًا، أو أنشئ له حسابًا بكلمة مرور من هذا النموذج.',
          );
        }
        account = await createLocalAccount({
          email,
          password: input.password,
          fullName: input.fullName?.trim() || email,
        });
      }
      const existing = (await core.rawFind('memberships', 'companyId', companyId)).find(
        (m) => m.userId === account.id && m.status !== 'ended',
      );
      if (existing) throw AppError.conflict('هذا المستخدم عضو بالفعل في المنشأة');

      if (input.primaryBranchId) {
        const branch = await core.rawGet('branches', input.primaryBranchId);
        if (!branch || branch.companyId !== companyId) throw AppError.validation('الفرع غير صالح');
      }

      const roles: Role[] = [];
      for (const roleId of unique(input.roleIds)) {
        const role = await core.rawGet('roles', roleId);
        if (!role || role.deletedAt || role.companyId !== companyId || role.isPlatformRole) {
          throw AppError.validation('الدور المحدد غير صالح لهذه المنشأة');
        }
        roles.push(role);
      }

      const now = core.now().toISOString();
      const membership: Membership = {
        id: newId(),
        companyId,
        userId: account.id,
        employeeNo: null,
        jobTitle: input.jobTitle?.trim() || null,
        status: 'active',
        isCompanyOwner: false,
        primaryBranchId: input.primaryBranchId,
        workingDays: [0, 1, 2, 3, 4, 6],
        joinedAt: now,
        endedAt: null,
        createdAt: now,
        updatedAt: now,
      };

      const profile = await core.rawGet('profiles', account.id);
      const db = await getLocalDb();
      return withTx(db, async (tx) => {
        if (!profile) {
          await storeOf(tx, 'profiles').put({
            id: account.id,
            email: account.email,
            fullName: input.fullName?.trim() || account.fullName,
            phone: null,
            avatarAttachmentId: null,
            avatarDataUrl: null,
            locale: 'ar',
            status: 'active',
            lastSeenAt: null,
            createdAt: now,
            updatedAt: now,
          } satisfies Profile);
        }
        await storeOf(tx, 'memberships').put(membership);
        await storeOf(tx, 'membership_profiles').put({
          id: membership.id, membershipId: membership.id, userId: account.id, companyId,
          fullName: input.fullName?.trim() || account.fullName, phone: null, avatarDataUrl: null,
          createdAt: now, updatedAt: now,
        });
        if (membership.primaryBranchId) {
          await storeOf(tx, 'membership_placements').put({
            id: newId(),
            companyId,
            membershipId: membership.id,
            scopeType: 'branch',
            scopeId: membership.primaryBranchId,
            isPrimary: true,
            createdAt: now,
            deletedAt: null,
            deletedBy: null,
            deletedReason: null,
          });
        }
        for (const role of roles) {
          await storeOf(tx, 'membership_roles').put({
            id: newId(),
            membershipId: membership.id,
            roleId: role.id,
            assignedBy: userId,
            assignedAt: now,
            expiresAt: null,
          });
        }
        await storeOf(tx, 'notifications').put(
          notificationRow({
            companyId,
            recipientUserId: account.id,
            type: 'membership.added',
            title: 'تمت إضافتك إلى المنشأة',
            body: roles.map((r) => r.name).join('، ') || null,
            taskId: null,
            now,
          }),
        );
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.membershipCreated,
              entityType: 'membership',
              entityId: membership.id,
              after: { userId: account.id, roles: roles.map((r) => r.id) },
            },
            now,
          ),
        );
        return membership;
      });
    },

    async completeTask(input: CompleteTaskInput): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.membershipId);

      const task = await loadTask(core, input.taskId);
      const subject = await subjectFor(core, task);
      const decision = canCompleteTask({ ctx: core.policy().access, subject });
      if (!decision.allowed) throw AppError.forbidden(decision.reason);
      if (task.status === 'completed' && task.approvalState !== 'returned') {
        throw AppError.conflict('المهمة مكتملة بالفعل');
      }
      if (task.requiresProof && input.attachmentIds.length === 0) {
        throw AppError.validation('هذه المهمة تتطلب إثباتًا. أرفق صورة أو مستندًا قبل الإكمال.');
      }

      const assignees = await core.rawFind('task_assignees', 'taskId', task.id);
      const own = assignees.find((a) => a.membershipId === input.membershipId && !a.unassignedAt);
      if (!own) throw AppError.forbidden('لست مسؤولًا عن هذه المهمة');

      const attachments: Attachment[] = [];
      for (const attachmentId of input.attachmentIds) {
        const attachment = await core.rawGet('attachments', attachmentId);
        if (!attachment || attachment.companyId !== companyId || attachment.taskId !== task.id) {
          throw AppError.validation('مرفق غير صالح لهذه المهمة');
        }
        attachments.push(attachment);
      }

      const at = core.now().toISOString();
      const updatedOwn: TaskAssignee = {
        ...own,
        state: 'completed',
        completedAt: at,
        completedByMembershipId: input.membershipId,
        proofNote: input.proofNote,
      };
      const finalAssignees = assignees
        .map((a) => (a.id === own.id ? updatedOwn : a))
        .filter((a) => !a.unassignedAt);
      const evaluation = evaluateCompletion({
        rule: task.completionRule,
        assignees: finalAssignees.map((a) => ({
          membershipId: a.membershipId,
          state: a.state,
          completedAt: a.completedAt,
        })),
        requiresApproval: task.requiresApproval,
      });

      let approverId = task.approverMembershipId;
      if (evaluation.taskComplete && evaluation.needsApproval && !approverId) {
        approverId = await resolveDefaultApprover(core, task);
      }
      const approverUserId = await recipientUserId(core, approverId);
      const db = await getLocalDb();

      const updatedTask = await withTx(db, async (tx) => {
        const tasks = storeOf(tx, 'tasks');
        const current = (await tasks.get(task.id)) as Task | undefined;
        if (!current || current.version !== task.version) {
          throw AppError.conflict('تم تعديل المهمة من جهة أخرى. أعد التحميل وحاول مرة أخرى.');
        }

        await storeOf(tx, 'task_assignees').put(updatedOwn);
        for (const attachment of attachments) {
          await storeOf(tx, 'attachments').put({
            ...attachment,
            taskAssigneeId: own.id,
            updatedAt: at,
          });
        }

        const next: Task = evaluation.taskComplete
          ? {
              ...current,
              ...completionPatch({ requiresApproval: evaluation.needsApproval, completedAt: at }),
              approverMembershipId: approverId,
              version: current.version + 1,
              updatedAt: at,
            }
          : { ...current, version: current.version + 1, updatedAt: at };

        await tasks.put(next);

        if (evaluation.taskComplete) {
          const history: TaskStatusHistory = {
            id: newId(),
            taskId: task.id,
            fromStatus: task.status,
            toStatus: next.status,
            fromApproval: task.approvalState,
            toApproval: next.approvalState,
            actorMembershipId: input.membershipId,
            actorUserId: userId,
            reason: null,
            createdAt: at,
          };
          await storeOf(tx, 'task_status_history').put(history);

          if (next.approvalState === 'pending' && approverUserId) {
            await storeOf(tx, 'notifications').put(
              notificationRow({
                companyId,
                recipientUserId: approverUserId,
                type: 'task.awaiting_approval',
                title: 'مهمة بانتظار اعتمادك',
                body: next.title,
                taskId: next.id,
                now: at,
              }),
            );
          }
        }

        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.membershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskCompleted,
              entityType: 'task',
              entityId: task.id,
              before: { status: task.status, approvalState: task.approvalState },
              after: {
                status: next.status,
                approvalState: next.approvalState,
                taskComplete: evaluation.taskComplete,
                remaining: evaluation.remainingCount,
              },
            },
            at,
          ),
        );

        return next;
      });

      return updatedTask;
    },

    async decideApproval(input): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.membershipId);

      const task = await loadTask(core, input.taskId);
      if (task.status !== 'completed' || task.approvalState !== 'pending') {
        throw AppError.conflict('لا يوجد إنجاز بانتظار قرار اعتماد');
      }
      const subject = await subjectFor(core, task);
      const decision = canApproveTask({
        ctx: core.policy().access,
        scope: scopeOf(core),
        subject,
      });
      if (!decision.allowed) throw AppError.forbidden(decision.reason);
      if (input.decision === 'return' && !input.reason?.trim()) {
        throw AppError.validation('سبب الإرجاع للتصحيح مطلوب');
      }

      const at = core.now().toISOString();
      const patch = approvalPatch({
        decision: input.decision,
        at,
        ...(input.reason ? { reason: input.reason } : {}),
      });

      const assigneeUserIds: Array<{ membershipId: string; userId: string }> = [];
      for (const membershipId of subject.assigneeMembershipIds) {
        const id = await recipientUserId(core, membershipId);
        if (id) assigneeUserIds.push({ membershipId, userId: id });
      }

      // تُقرأ بيانات الإسناد قبل بدء معاملة IndexedDB: انتظار أي طلب خارج
      // المعاملة أثناءها يجعل المتصفح يغلقها تلقائيًا.
      const assigneesToReset =
        input.decision === 'return'
          ? (await core.rawFind('task_assignees', 'taskId', task.id)).filter((a) => !a.unassignedAt)
          : [];

      const db = await getLocalDb();
      const updated = await withTx(db, async (tx) => {
        const tasks = storeOf(tx, 'tasks');
        const current = (await tasks.get(task.id)) as Task | undefined;
        if (!current || current.version !== task.version) {
          throw AppError.conflict('تم تعديل المهمة من جهة أخرى. أعد التحميل وحاول مرة أخرى.');
        }

        const next: Task = {
          ...current,
          approvalState: patch.approvalState,
          approvedAt: patch.approvedAt,
          returnedAt: patch.returnedAt,
          returnedReason: patch.returnedReason,
          status: patch.status,
          approvedByMembershipId: input.decision === 'approve' ? input.membershipId : null,
          returnedByMembershipId: input.decision === 'return' ? input.membershipId : null,
          version: current.version + 1,
          updatedAt: at,
        };
        await tasks.put(next);

        for (const assignee of assigneesToReset) {
          await storeOf(tx, 'task_assignees').put({ ...assignee, state: 'pending' });
        }

        await storeOf(tx, 'task_status_history').put({
          id: newId(),
          taskId: task.id,
          fromStatus: task.status,
          toStatus: next.status,
          fromApproval: task.approvalState,
          toApproval: next.approvalState,
          actorMembershipId: input.membershipId,
          actorUserId: userId,
          reason: input.reason,
          createdAt: at,
        } satisfies TaskStatusHistory);

        for (const assignee of assigneeUserIds) {
          await storeOf(tx, 'notifications').put(
            notificationRow({
              companyId,
              recipientUserId: assignee.userId,
              type: input.decision === 'approve' ? 'task.approved' : 'task.returned',
              title: input.decision === 'approve' ? 'تم اعتماد إنجاز مهمة' : 'أُعيدت مهمة للتصحيح',
              body:
                input.decision === 'return'
                  ? `${task.title} — ${input.reason ?? ''}`
                  : task.title,
              taskId: task.id,
              now: at,
            }),
          );
        }

        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.membershipId,
            {
              companyId,
              action:
                input.decision === 'approve' ? AUDIT_ACTIONS.taskApproved : AUDIT_ACTIONS.taskReturned,
              entityType: 'task',
              entityId: task.id,
              before: { approvalState: task.approvalState },
              after: { approvalState: next.approvalState, reason: input.reason },
            },
            at,
          ),
        );

        return next;
      });

      return updated;
    },

    async reopenTask(input): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.membershipId);
      requirePermission(core, 'task.reopen');

      const task = await loadTask(core, input.taskId);
      if (task.status !== 'completed') throw AppError.conflict('المهمة ليست مكتملة');
      if (task.approvalState === 'pending') {
        throw AppError.conflict('المهمة بانتظار الاعتماد، استخدم قرار الاعتماد');
      }
      const subject = await subjectFor(core, task);
      const editable = canEditTask({
        ctx: core.policy().access,
        scope: scopeOf(core),
        subject,
      });
      if (!editable.allowed) throw AppError.forbidden(editable.reason);

      const at = core.now().toISOString();
      const assignees = (await core.rawFind('task_assignees', 'taskId', task.id)).filter(
        (a) => !a.unassignedAt,
      );
      const db = await getLocalDb();

      return withTx(db, async (tx) => {
        const tasks = storeOf(tx, 'tasks');
        const current = (await tasks.get(task.id)) as Task | undefined;
        if (!current || current.version !== task.version) {
          throw AppError.conflict('تم تعديل المهمة من جهة أخرى');
        }
        const next: Task = {
          ...current,
          status: 'in_progress',
          approvalState: 'not_required',
          completedAt: null,
          approvedAt: null,
          approvedByMembershipId: null,
          version: current.version + 1,
          updatedAt: at,
        };
        await tasks.put(next);
        for (const assignee of assignees) {
          await storeOf(tx, 'task_assignees').put({ ...assignee, state: 'pending' });
        }
        await storeOf(tx, 'task_status_history').put({
          id: newId(),
          taskId: task.id,
          fromStatus: task.status,
          toStatus: 'in_progress',
          fromApproval: task.approvalState,
          toApproval: 'not_required',
          actorMembershipId: input.membershipId,
          actorUserId: userId,
          reason: input.reason,
          createdAt: at,
        } satisfies TaskStatusHistory);
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.membershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskReopened,
              entityType: 'task',
              entityId: task.id,
              after: { reason: input.reason },
            },
            at,
          ),
        );
        return next;
      });
    },

    async cancelTask(input): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requireMembership(core, input.membershipId);
      requirePermission(core, 'task.cancel');

      const task = await loadTask(core, input.taskId);
      if (task.status === 'cancelled') return task;
      if (task.status === 'completed') throw AppError.conflict('لا يمكن إلغاء مهمة مكتملة');

      const subject = await subjectFor(core, task);
      if (
        !canEditTask({ ctx: core.policy().access, scope: scopeOf(core), subject }).allowed
      ) {
        throw AppError.forbidden('لا تملك صلاحية إلغاء هذه المهمة');
      }

      const at = core.now().toISOString();
      const db = await getLocalDb();
      return withTx(db, async (tx) => {
        const tasks = storeOf(tx, 'tasks');
        const current = (await tasks.get(task.id)) as Task | undefined;
        if (!current || current.version !== task.version) {
          throw AppError.conflict('تم تعديل المهمة من جهة أخرى');
        }
        const next: Task = {
          ...current,
          status: 'cancelled',
          cancelledAt: at,
          cancelledByMembershipId: input.membershipId,
          version: current.version + 1,
          updatedAt: at,
        };
        await tasks.put(next);
        await storeOf(tx, 'task_status_history').put({
          id: newId(),
          taskId: task.id,
          fromStatus: task.status,
          toStatus: 'cancelled',
          fromApproval: task.approvalState,
          toApproval: task.approvalState,
          actorMembershipId: input.membershipId,
          actorUserId: userId,
          reason: input.reason,
          createdAt: at,
        } satisfies TaskStatusHistory);
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.membershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskCancelled,
              entityType: 'task',
              entityId: task.id,
              after: { reason: input.reason },
            },
            at,
          ),
        );
        return next;
      });
    },

    async deleteTask(input): Promise<void> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      if (input.actorUserId !== userId) {
        throw AppError.forbidden('لا يمكنك تنفيذ الإجراء باسم مستخدم آخر');
      }

      const task = await loadTask(core, input.taskId);
      const subject = await subjectFor(core, task);
      const decision = canDeleteTask({
        ctx: core.policy().access,
        scope: scopeOf(core),
        subject,
      });
      if (!decision.allowed) throw AppError.forbidden(decision.reason);

      const [attachments, assignees, steps, history, notifications, allSettings] = await Promise.all([
        core.rawFind('attachments', 'taskId', task.id),
        core.rawFind('task_assignees', 'taskId', task.id),
        core.rawFind('task_steps', 'taskId', task.id),
        core.rawFind('task_status_history', 'taskId', task.id),
        core.rawFind('notifications', 'taskId', task.id),
        core.rawAll('settings'),
      ]);

      for (const attachment of attachments) {
        if (attachment.storagePath && attachment.uploadState === 'uploaded' && !attachment.deletedAt) {
          try {
            await core.blob.remove(attachment.storagePath);
          } catch {
            // نتجاهل فشل حذف الملف المحلي الفردي لإتمام حذف السجل.
          }
        }
      }

      const at = core.now().toISOString();
      const actorMembershipId = core.policy().membershipId ?? null;
      const existingRetentionRecord = (allSettings as SettingRecord[]).find(
        (s) =>
          s.scopeType === 'company' &&
          s.scopeId === companyId &&
          s.key === SETTING_KEYS.completedTaskRetention,
      );
      const currentRetention: CompletedTaskRetentionPolicy = {
        ...DEFAULT_COMPLETED_TASK_RETENTION,
        ...(existingRetentionRecord?.value as Partial<CompletedTaskRetentionPolicy> | undefined),
      };

      const db = await getLocalDb();
      await withTx(db, async (tx) => {
        for (const row of attachments) await storeOf(tx, 'attachments').delete(row.id);
        for (const row of assignees) await storeOf(tx, 'task_assignees').delete(row.id);
        for (const row of steps) await storeOf(tx, 'task_steps').delete(row.id);
        for (const row of history) await storeOf(tx, 'task_status_history').delete(row.id);
        for (const row of notifications) await storeOf(tx, 'notifications').delete(row.id);
        await storeOf(tx, 'tasks').delete(task.id);

        if (task.status === 'completed') {
          const updatedRetention: CompletedTaskRetentionPolicy = {
            ...currentRetention,
            archivedCompletedCount: Math.max(0, Number(currentRetention.archivedCompletedCount ?? 0)) + 1,
          };
          const settingRow: SettingRecord = existingRetentionRecord
            ? {
                ...existingRetentionRecord,
                value: updatedRetention,
                updatedBy: userId,
                updatedAt: at,
              }
            : {
                id: newId(),
                scopeType: 'company',
                scopeId: companyId,
                key: SETTING_KEYS.completedTaskRetention,
                value: updatedRetention,
                updatedBy: userId,
                updatedAt: at,
              };
          await storeOf(tx, 'settings').put(settingRow);
        }

        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskDeleted,
              entityType: 'task',
              entityId: task.id,
              before: {
                title: task.title,
                status: task.status,
                approvalState: task.approvalState,
                attachmentsCount: attachments.length,
              },
            },
            at,
          ),
        );
      });

      await core.refresh();
    },

    async purgeCompletedTasks(input: PurgeCompletedTasksInput): Promise<PurgeCompletedTasksResult> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      if (input.companyId !== companyId) {
        throw AppError.forbidden('لا يمكن تنظيف مهام منشأة أخرى');
      }
      if (input.actorUserId !== userId) {
        throw AppError.forbidden('لا يمكنك تنفيذ الإجراء باسم مستخدم آخر');
      }

      const allSettings = (await core.rawAll('settings')) as SettingRecord[];
      const existingRetentionRecord = allSettings.find(
        (s) =>
          s.scopeType === 'company' &&
          s.scopeId === companyId &&
          s.key === SETTING_KEYS.completedTaskRetention,
      );
      const currentRetention: CompletedTaskRetentionPolicy = {
        ...DEFAULT_COMPLETED_TASK_RETENTION,
        ...(existingRetentionRecord?.value as Partial<CompletedTaskRetentionPolicy> | undefined),
      };

      const ctx = core.policy();
      const isAuthorized =
        ctx.isPlatformOwner ||
        ctx.access.membership?.membership.isCompanyOwner === true ||
        ctx.permissions.has('task.delete') ||
        ctx.permissions.has('company.settings.manage') ||
        ctx.permissions.has('attachment.retention.manage') ||
        currentRetention.autoDeleteEnabled;
      if (!isAuthorized) {
        throw AppError.forbidden('لا تملك صلاحية تنظيف المهام المكتملة');
      }

      const days = Math.max(0, Math.floor(Number(input.olderThanDays ?? 0)));
      const cutoffMs = core.now().getTime() - days * 86_400_000;

      const companyTasks = await core.rawFind('tasks', 'companyId', companyId);
      const eligibleTasks = companyTasks.filter((t) => {
        if (t.deletedAt || t.status !== 'completed') return false;
        if (days <= 0) return true;
        const completedRef = t.completedAt ?? t.approvedAt ?? t.updatedAt ?? t.createdAt;
        const parsed = Date.parse(completedRef);
        return !Number.isNaN(parsed) && parsed <= cutoffMs;
      });

      if (eligibleTasks.length === 0) {
        return { tasksCount: 0, filesCount: 0, bytesFreed: 0 };
      }

      const taskIds = new Set(eligibleTasks.map((t) => t.id));
      const companyAttachments = await core.rawFind('attachments', 'companyId', companyId);
      const matchingAttachments = companyAttachments.filter(
        (a) => a.taskId !== null && taskIds.has(a.taskId),
      );
      const activeAttachments = matchingAttachments.filter(
        (a) => a.uploadState === 'uploaded' && !a.deletedAt,
      );
      const bytesFreed = activeAttachments.reduce((sum, a) => sum + (a.sizeBytes ?? 0), 0);

      if (input.dryRun) {
        return {
          tasksCount: eligibleTasks.length,
          filesCount: activeAttachments.length,
          bytesFreed,
        };
      }

      for (const attachment of activeAttachments) {
        if (attachment.storagePath) {
          try {
            await core.blob.remove(attachment.storagePath);
          } catch {
            // نتجاهل خطأ الملف الفردي لإتمام تنظيف بقية المهام.
          }
        }
      }

      const [allAssignees, allSteps, allHistory, allNotifications] = await Promise.all([
        core.rawAll('task_assignees'),
        core.rawAll('task_steps'),
        core.rawAll('task_status_history'),
        core.rawFind('notifications', 'companyId', companyId),
      ]);

      const assigneesToDelete = allAssignees.filter((r) => taskIds.has(r.taskId));
      const stepsToDelete = allSteps.filter((r) => taskIds.has(r.taskId));
      const historyToDelete = allHistory.filter((r) => taskIds.has(r.taskId));
      const notificationsToDelete = allNotifications.filter(
        (r) => r.taskId !== null && taskIds.has(r.taskId),
      );

      const at = core.now().toISOString();
      const actorMembershipId = ctx.membershipId ?? null;
      const updatedRetention: CompletedTaskRetentionPolicy = {
        ...currentRetention,
        archivedCompletedCount:
          Math.max(0, Number(currentRetention.archivedCompletedCount ?? 0)) + eligibleTasks.length,
      };

      const db = await getLocalDb();
      await withTx(db, async (tx) => {
        for (const row of matchingAttachments) await storeOf(tx, 'attachments').delete(row.id);
        for (const row of assigneesToDelete) await storeOf(tx, 'task_assignees').delete(row.id);
        for (const row of stepsToDelete) await storeOf(tx, 'task_steps').delete(row.id);
        for (const row of historyToDelete) await storeOf(tx, 'task_status_history').delete(row.id);
        for (const row of notificationsToDelete) await storeOf(tx, 'notifications').delete(row.id);
        for (const task of eligibleTasks) await storeOf(tx, 'tasks').delete(task.id);

        const settingRow: SettingRecord = existingRetentionRecord
          ? {
              ...existingRetentionRecord,
              value: updatedRetention,
              updatedBy: userId,
              updatedAt: at,
            }
          : {
              id: newId(),
              scopeType: 'company',
              scopeId: companyId,
              key: SETTING_KEYS.completedTaskRetention,
              value: updatedRetention,
              updatedBy: userId,
              updatedAt: at,
            };
        await storeOf(tx, 'settings').put(settingRow);

        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.completedTasksPurged,
              entityType: 'company',
              entityId: companyId,
              after: {
                tasksCount: eligibleTasks.length,
                filesCount: activeAttachments.length,
                bytesFreed,
                olderThanDays: days,
                archivedCompletedCount: updatedRetention.archivedCompletedCount,
              },
            },
            at,
          ),
        );
      });

      await core.refresh();
      return {
        tasksCount: eligibleTasks.length,
        filesCount: activeAttachments.length,
        bytesFreed,
      };
    },

    async assignTask(input): Promise<Task> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      const task = await loadTask(core, input.taskId);
      const subject = await subjectFor(core, task);
      const scope = scopeOf(core);

      const permission = canAssignTask({
        ctx: core.policy().access,
        scope,
        subject,
      });
      if (!permission.allowed) throw AppError.forbidden(permission.reason);

      const targets: Membership[] = [];
      for (const membershipId of input.membershipIds) {
        const membership = await core.rawGet('memberships', membershipId);
        if (!membership || membership.status !== 'active') {
          throw AppError.validation('العضوية المحددة غير نشطة');
        }
        const decision = canAssignToMembership({
          ctx: core.policy().access,
          scope,
          targetMembership: { id: membership.id, companyId: membership.companyId },
        });
        if (!decision.allowed) throw AppError.forbidden(decision.reason);
        targets.push(membership);
      }
      if (targets.length === 0) throw AppError.validation('اختر مسؤولًا واحدًا على الأقل');

      const at = core.now().toISOString();
      const existing = await core.rawFind('task_assignees', 'taskId', task.id);
      const targetIds = new Set(targets.map((t) => t.id));
      const targetUserIds: Array<{ membershipId: string; userId: string }> = [];
      for (const target of targets) {
        if (!existing.some((a) => a.membershipId === target.id && !a.unassignedAt)) {
          targetUserIds.push({ membershipId: target.id, userId: target.userId });
        }
      }

      const db = await getLocalDb();
      return withTx(db, async (tx) => {
        const tasks = storeOf(tx, 'tasks');
        const current = (await tasks.get(task.id)) as Task | undefined;
        if (!current || current.version !== task.version) {
          throw AppError.conflict('تم تعديل المهمة من جهة أخرى');
        }

        for (const assignee of existing) {
          if (!assignee.unassignedAt && !targetIds.has(assignee.membershipId)) {
            await storeOf(tx, 'task_assignees').put({
              ...assignee,
              unassignedAt: at,
              unassignedReason: 'تغيير المسؤولين',
            });
          }
        }
        for (const target of targets) {
          if (existing.some((a) => a.membershipId === target.id && !a.unassignedAt)) continue;
          const row: TaskAssignee = {
            id: newId(),
            taskId: task.id,
            membershipId: target.id,
            state: 'pending',
            assignedAt: at,
            assignedByMembershipId: input.actorMembershipId,
            unassignedAt: null,
            unassignedReason: null,
            completedAt: null,
            completedByMembershipId: null,
            proofNote: null,
          };
          await storeOf(tx, 'task_assignees').put(row);
        }
        for (const target of targetUserIds) {
          await storeOf(tx, 'notifications').put(
            notificationRow({
              companyId,
              recipientUserId: target.userId,
              type: 'task.assigned',
              title: 'أُسندت إليك مهمة جديدة',
              body: task.title,
              taskId: task.id,
              now: at,
            }),
          );
        }

        const next: Task = { ...current, version: current.version + 1, updatedAt: at };
        await tasks.put(next);
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskAssigned,
              entityType: 'task',
              entityId: task.id,
              after: { membershipIds: targets.map((t) => t.id) },
            },
            at,
          ),
        );
        return next;
      });
    },

    async generateOccurrences(input: GenerateOccurrencesInput): Promise<GenerateOccurrencesResult> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      const template = await core.rawGet('task_templates', input.templateId);
      if (!template || template.deletedAt || template.companyId !== companyId) {
        throw AppError.notFound('قالب المهمة غير موجود');
      }
      if (template.status !== 'active') {
        return { created: 0, skipped: 0, lastDate: template.generatedThrough ?? template.startDate };
      }

      const issues = validateRule(template.recurrence, template.startDate);
      if (issues.length > 0) {
        throw AppError.validation(`قاعدة التكرار غير صالحة: ${issues.map((i) => i.message).join('، ')}`);
      }

      const fromDate = template.generatedThrough
        ? addDaysLocal(template.generatedThrough, 1)
        : template.startDate;
      if (input.throughDate < fromDate) {
        return {
          created: 0,
          skipped: 0,
          lastDate: template.generatedThrough ?? template.startDate,
        };
      }

      const planned = planOccurrences({
        templateId: template.id,
        rule: template.recurrence,
        startDate: template.startDate,
        startTime: template.startTime,
        durationMinutes: template.durationMinutes,
        timeZone: template.timeZone,
        fromDate,
        throughDate: input.throughDate,
      });

      const assigneeIds = await resolveTemplateAssignees(core, template);
      const db = await getLocalDb();
      let created = 0;
      let skipped = 0;

      for (const occurrence of planned) {
        const existing = await core.rawFind('tasks', 'occurrenceKey', occurrence.occurrenceKey);
        if (existing.some((t) => !t.deletedAt)) {
          skipped += 1;
          continue;
        }
        const at = core.now().toISOString();
        const task: Task = {
          id: newId(),
          companyId: template.companyId,
          brandId: template.brandId,
          branchId: template.branchId,
          categoryId: template.categoryId,
          templateId: template.id,
          occurrenceKey: occurrence.occurrenceKey,
          title: occurrence.localTime ? `${template.title} — ${occurrence.localTime}` : template.title,
          description: template.description,
          instructions: template.instructions,
          taskType: template.taskType,
          priority: template.priority,
          status: 'scheduled',
          approvalState: 'not_required',
          assignmentMode: template.assignmentMode,
          completionRule: template.completionRule,
          requiresProof: template.requiresProof,
          requiresApproval: template.requiresApproval,
          approverMembershipId: template.approverMembershipId,
          createdByMembershipId: template.createdByMembershipId,
          isPersonal: template.isPersonal,
          visibility: template.visibility,
          assigneeScope: {
            branchIds: template.branchId ? [template.branchId] : [],
            brandIds: template.brandId ? [template.brandId] : [],
            membershipIds: assigneeIds,
            includeSupervised: false,
          },
          startAt: occurrence.startAt,
          dueAt: occurrence.dueAt,
          completedAt: null,
          approvedAt: null,
          approvedByMembershipId: null,
          returnedAt: null,
          returnedByMembershipId: null,
          returnedReason: null,
          cancelledAt: null,
          cancelledByMembershipId: null,
          localDate: occurrence.localDate,
          timeZone: template.timeZone,
          version: 1,
          createdAt: at,
          updatedAt: at,
          deletedAt: null,
        };

        try {
          await withTx(db, async (tx) => {
            await storeOf(tx, 'tasks').put(task);
            for (const membershipId of assigneeIds) {
              await storeOf(tx, 'task_assignees').put({
                id: newId(),
                taskId: task.id,
                membershipId,
                state: 'pending',
                assignedAt: at,
                assignedByMembershipId: template.createdByMembershipId,
                unassignedAt: null,
                unassignedReason: null,
                completedAt: null,
                completedByMembershipId: null,
                proofNote: null,
              } satisfies TaskAssignee);
            }
          });
          created += 1;
        } catch {
          skipped += 1;
        }
      }

      const lastPlanned = planned.length > 0
        ? planned.reduce((last, occurrence) => {
            const scheduleDate = occurrence.scheduleDate ?? occurrence.localDate;
            return scheduleDate > last ? scheduleDate : last;
          }, fromDate)
        : input.throughDate;
      const newGeneratedThrough =
        template.generatedThrough && template.generatedThrough > lastPlanned
          ? template.generatedThrough
          : lastPlanned;
      await core.table('task_templates').update(template.id, {
        generatedThrough: newGeneratedThrough,
        updatedAt: core.now().toISOString(),
      });

      await core.audit({
        companyId,
        action: AUDIT_ACTIONS.taskOccurrencesGenerated,
        entityType: 'task_template',
        entityId: template.id,
        after: { created, skipped, fromDate, throughDate: input.throughDate, by: userId },
      });

      return { created, skipped, lastDate: newGeneratedThrough };
    },

    async createCompany(input: CreateCompanyInput): Promise<Company> {
      const userId = requireUser(core);
      if (!core.policy().isPlatformOwner) {
        throw AppError.forbidden('إنشاء المنشآت متاح لمالك المنصة فقط');
      }
      const now = core.now().toISOString();
      const company: Company = {
        ...input.company,
        id: newId(),
        settings: input.company.settings ?? {},
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };
      await core.rawPut('companies', company);

      if (input.seedDefaults) {
        await seedCompanyStructure(core, { company, ownerUserId: userId, now, createOwnerMembership: input.createOwnerMembership ?? true });
      }

      await core.audit({
        companyId: company.id,
        action: AUDIT_ACTIONS.companyCreated,
        entityType: 'company',
        entityId: company.id,
        after: { name: company.name, slug: company.slug },
      });
      await core.refresh();
      return company;
    },

    async manageOrgEntity(input: OrgLifecycleInput): Promise<void> {
      await manageOrgEntityLocal(core, input);
      await core.refresh();
    },

    async listOrgArchive(input: OrgArchiveQuery): Promise<ArchivedOrgRecord[]> {
      return listOrgArchiveLocal(core, input);
    },

    async updateMembershipProfile(input): Promise<void> {
      const ctx = core.policy();
      if (!ctx.userId) throw AppError.unauthenticated();
      if (input.companyId !== ctx.companyId || !ctx.permissions.has('membership.manage')) {
        throw AppError.forbidden('تعديل بيانات الموظف يتطلب صلاحية إدارة العضويات في المنشأة');
      }
      const fullName = input.fullName.trim();
      const jobTitle = input.jobTitle?.trim() || null;
      if (fullName.length < 2 || fullName.length > 120) {
        throw AppError.validation('الاسم يجب أن يكون بين حرفين و120 حرفًا');
      }
      if (jobTitle && jobTitle.length > 120) {
        throw AppError.validation('المسمى الوظيفي أطول من الحد المسموح');
      }
      const membership = await core.rawGet('memberships', input.membershipId);
      if (!membership || membership.companyId !== input.companyId || membership.deletedAt) {
        throw AppError.notFound('العضوية غير موجودة أو محذوفة');
      }
      if (ctx.scope?.companyWide !== true && !ctx.scope?.membershipIds.has(membership.id)) {
        const placements = (await core.rawFind('membership_placements', 'membershipId', membership.id))
          .filter((placement) => !placement.deletedAt);
        const branchIds = new Set<string>([
          ...(membership.primaryBranchId ? [membership.primaryBranchId] : []),
          ...placements.filter((placement) => placement.scopeType === 'branch').map((placement) => placement.scopeId),
        ]);
        const brandIds = new Set<string>(
          placements.filter((placement) => placement.scopeType === 'brand').map((placement) => placement.scopeId),
        );
        for (const branchId of branchIds) {
          const branch = await core.rawGet('branches', branchId);
          if (branch?.brandId) brandIds.add(branch.brandId);
        }
        const covered = [...branchIds].some((branchId) => ctx.scope?.branchIds.has(branchId)) ||
          [...brandIds].some((brandId) => ctx.scope?.brandIds.has(brandId));
        if (!covered) throw AppError.forbidden('الموظف خارج نطاقك');
      }

      const now = new Date().toISOString();
      const profile = await core.rawGet('membership_profiles', membership.id);
      const accountProfile = await core.rawGet('profiles', membership.userId);
      await core.rawPut('membership_profiles', {
        id: membership.id,
        membershipId: membership.id,
        userId: membership.userId,
        companyId: membership.companyId,
        fullName,
        phone: profile?.phone ?? accountProfile?.phone ?? null,
        avatarDataUrl: profile?.avatarDataUrl ?? accountProfile?.avatarDataUrl ?? null,
        createdAt: profile?.createdAt ?? now,
        updatedAt: now,
      });
      await core.rawPut('memberships', { ...membership, jobTitle, updatedAt: now });
      await core.audit({
        companyId: membership.companyId,
        action: 'membership.updated',
        entityType: 'membership',
        entityId: membership.id,
        before: { fullName: profile?.fullName ?? accountProfile?.fullName ?? null, jobTitle: membership.jobTitle },
        after: { fullName, jobTitle },
      });
      await core.refresh();
    },

    async purgeAuditLogs(input): Promise<{
      auditLogsDeleted: number;
      supportAuditDeleted: number;
      supportSessionsDeleted: number;
      activeSupportSessionsRetained: number;
    }> {
      const ctx = core.policy();
      if (!ctx.isPlatformOwner) throw AppError.forbidden('حذف سجل التدقيق متاح لمالك المنصة فقط');
      if (input.confirmation !== 'حذف سجل نشاط المنصة نهائيًا') {
        throw AppError.validation('عبارة التأكيد غير مطابقة');
      }
      const rows = await core.rawAll('audit_logs');
      for (const row of rows) await core.rawDelete('audit_logs', row.id);
      await core.refresh();
      return {
        auditLogsDeleted: rows.length,
        supportAuditDeleted: 0,
        supportSessionsDeleted: 0,
        activeSupportSessionsRetained: 0,
      };
    },

    async purgeCompanyActivityLogs(input) {
      const ctx = core.policy();
      if (
        ctx.companyId !== input.companyId ||
        (!ctx.isPlatformOwner && !ctx.permissions.has('company.manage'))
      ) {
        throw AppError.forbidden('حذف سجل نشاط هذه المنشأة غير مصرح به');
      }
      if (input.confirmation !== 'حذف سجل نشاط المنشأة نهائيًا') {
        throw AppError.validation('عبارة التأكيد غير مطابقة');
      }
      const companyRows = await core.rawFind('audit_logs', 'companyId', input.companyId);
      const rows = companyRows.filter((row) => !row.action.startsWith('platform.support.'));
      const supportAuditRetained = companyRows.length - rows.length;
      for (const row of rows) await core.rawDelete('audit_logs', row.id);
      await core.refresh();
      return { auditLogsDeleted: rows.length, supportAuditRetained, activeSupportSessionsRetained: 0 };
    },

    async listPlatformActivity(input = {}): Promise<PlatformActivityEvent[]> {
      if (!core.policy().isPlatformOwner) {
        throw AppError.forbidden('سجل نشاط المنصة متاح لمالك المنصة فقط');
      }
      await purgeExpiredLocalActivity(core);
      const [logs, companies, profiles, membershipProfiles] = await Promise.all([
        core.rawAll('audit_logs'),
        core.rawAll('companies'),
        core.rawAll('profiles'),
        core.rawAll('membership_profiles'),
      ]);
      const companyNames = new Map(companies.map((company) => [company.id, company.name]));
      const profileNames = new Map(profiles.map((profile) => [profile.id, profile.fullName]));
      const membershipNames = new Map(membershipProfiles.map((profile) => [profile.membershipId, profile.fullName]));
      const limit = Math.max(1, Math.min(input.limit ?? 300, 500));
      return logs
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
        .slice(0, limit)
        .map((event) => ({
          id: `organization:${event.id}`,
          companyId: event.companyId,
          companyName: event.companyId ? companyNames.get(event.companyId) ?? null : null,
          actorUserId: event.actorUserId,
          actorName: event.actorMembershipId
            ? membershipNames.get(event.actorMembershipId) ?? 'عضوية محذوفة'
            : event.actorUserId
              ? profileNames.get(event.actorUserId) ?? 'مالك المنصة'
              : 'النظام',
          actorMembershipId: event.actorMembershipId,
          action: event.action,
          entityType: event.entityType,
          entityId: event.entityId,
          severity: event.severity,
          createdAt: event.createdAt,
          source: event.action.startsWith('platform.support.') ? 'support' : 'organization',
        }));
    },

    async getPlatformActivityRetention(): Promise<PlatformActivityRetentionSettings> {
      if (!core.policy().isPlatformOwner) {
        throw AppError.forbidden('إعداد الاحتفاظ بسجل النشاط متاح لمالك المنصة فقط');
      }
      return localActivityRetention();
    },

    async setPlatformActivityRetention(input): Promise<PlatformActivityRetentionSettings> {
      const ctx = core.policy();
      if (!ctx.isPlatformOwner) {
        throw AppError.forbidden('إعداد الاحتفاظ بسجل النشاط متاح لمالك المنصة فقط');
      }
      if (input.enabled && (!Number.isInteger(input.retentionDays) || input.retentionDays! < 1 || input.retentionDays! > 3650)) {
        throw AppError.validation('اختر مدة من يوم واحد إلى 3650 يومًا');
      }
      if (input.retentionDays !== null && (!Number.isInteger(input.retentionDays) || input.retentionDays < 1 || input.retentionDays > 3650)) {
        throw AppError.validation('مدة الاحتفاظ يجب أن تكون بين يوم واحد و3650 يومًا');
      }
      const setting: PlatformActivityRetentionSettings = {
        enabled: input.enabled,
        retentionDays: input.retentionDays,
        updatedAt: core.now().toISOString(),
        updatedBy: ctx.userId,
      };
      setDeviceSetting(LOCAL_ACTIVITY_RETENTION_KEY, setting);
      if (setting.enabled) await purgeExpiredLocalActivity(core);
      return setting;
    },

    async bootstrapStatus(): Promise<BootstrapStatus> {
      const owners = await core.rawAll('platform_owners');
      const initialized = owners.some((o) => !o.revokedAt);
      const settings = await readPlatformOperationsSettings(core);
      return {
        initialized,
        canBootstrap: !initialized,
        showBootstrapLink: getLocalBootstrapLinkVisibility(),
        method: 'local_token',
        reason: initialized
          ? 'تمت تهيئة مالك المنصة مسبقًا.'
          : 'تهيئة أولى: تحتاج رمز التهيئة الذي تحدده في الخطوة الأولى على هذا الجهاز.',
        branding: getLocalPlatformBranding(),
        theme: getLocalPlatformTheme(),
        selfRegistrationEnabled: settings.selfRegistrationEnabled,
        subscriptionsEnabled: settings.subscriptionsEnabled,
      };
    },

    async setBootstrapLinkVisibility(input: { visible: boolean }): Promise<void> {
      const userId = requireUser(core);
      const owners = await core.rawAll('platform_owners');
      if (!owners.some((owner) => owner.userId === userId && !owner.revokedAt)) {
        throw AppError.forbidden('إظهار أو إخفاء رابط التهيئة متاح لمالك المنصة فقط');
      }
      setLocalBootstrapLinkVisibility(input.visible);
    },

    async setPlatformBranding(input: {
      branding: PlatformBranding;
      theme: ThemeTokens | null;
    }): Promise<void> {
      const userId = requireUser(core);
      const owners = await core.rawAll('platform_owners');
      if (!owners.some((owner) => owner.userId === userId && !owner.revokedAt)) {
        throw AppError.forbidden('تعديل هوية المنصة متاح لمالك المنصة فقط');
      }
      setLocalPlatformBranding(input.branding, input.theme);
    },

    async getPlatformOperationsSettings() {
      return getPlatformOperationsSettingsLocal(core);
    },

    async setPlatformOperationsSettings(input) {
      const settings = await setPlatformOperationsSettingsLocal(core, input);
      await core.refresh();
      return settings;
    },

    async selfRegisterCompany(input) {
      return selfRegisterCompanyLocal(core, input);
    },

    async createCompanyInvite(input) {
      const result = await createCompanyInviteLocal(core, input);
      await core.refresh();
      return result;
    },

    async acceptCompanyInvite(input) {
      return acceptCompanyInviteLocal(core, input);
    },

    async saveSubscriptionPlan(input) {
      const plan = await saveSubscriptionPlanLocal(core, input);
      await core.refresh();
      return plan;
    },

    async saveCompanySubscription(input) {
      const subscription = await saveCompanySubscriptionLocal(core, input);
      await core.refresh();
      return subscription;
    },

    async recordSubscriptionPayment(input) {
      const payment = await recordSubscriptionPaymentLocal(core, input);
      await core.refresh();
      return payment;
    },

    async updateSubscriptionPayment(input) {
      const payment = await updateSubscriptionPaymentLocal(core, input);
      await core.refresh();
      return payment;
    },

    async listDeletedCompanyRoles(input): Promise<Role[]> {
      const companyId = requireCompany(core);
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن عرض محذوفات أدوار منشأة أخرى');
      requireCompanyRoleManager(core, companyId);
      return (await core.rawFind('roles', 'companyId', companyId))
        .filter((role) => role.deletedAt !== null)
        .sort((left, right) => String(right.deletedAt).localeCompare(String(left.deletedAt)));
    },

    async restoreCompanyRole(input): Promise<Role> {
      const companyId = requireCompany(core);
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن استعادة دور في منشأة أخرى');
      requireCompanyRoleManager(core, companyId);
      const ctx = core.policy();
      const at = core.now().toISOString();
      const db = await getLocalDb();
      let restoredRole: Role | null = null;

      await withTx(db, async (tx) => {
        const rolesStore = storeOf(tx, 'roles');
        const auditStore = storeOf(tx, 'audit_logs');
        const value = await rolesStore.get(input.roleId);
        const role = value as Role | undefined;
        if (!role || role.companyId !== companyId || !role.deletedAt) {
          throw AppError.notFound('الدور غير موجود في سلة المحذوفات');
        }
        restoredRole = { ...role, deletedAt: null, updatedAt: at };
        await rolesStore.put(restoredRole);
        await auditStore.put(auditRow(ctx.userId, ctx.membershipId, {
          companyId,
          action: AUDIT_ACTIONS.roleRestored,
          entityType: 'role',
          entityId: role.id,
          before: { name: role.name, deletedAt: role.deletedAt },
          after: { restoredAt: at, assignmentsRestored: false, hierarchyReferencesRestored: false },
        }, at));
      });
      await core.refresh();
      if (!restoredRole) throw AppError.notFound('الدور غير موجود في سلة المحذوفات');
      return restoredRole;
    },

    async getRoleDeletionImpact(input): Promise<RoleDeletionImpact> {
      const companyId = requireCompany(core);
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن معاينة دور في منشأة أخرى');
      requireCompanyRoleManager(core, companyId);
      const role = await core.rawGet('roles', input.roleId);
      if (!role || role.companyId !== companyId || role.deletedAt) throw AppError.notFound('الدور غير موجود');

      const [memberships, assignments, profiles, roles] = await Promise.all([
        core.rawFind('memberships', 'companyId', companyId),
        core.rawFind('membership_roles', 'roleId', role.id),
        core.rawFind('membership_profiles', 'companyId', companyId),
        core.rawFind('roles', 'companyId', companyId),
      ]);
      const membershipById = new Map(memberships.map((membership) => [membership.id, membership]));
      const profileByMembershipId = new Map(profiles.map((profile) => [profile.membershipId, profile]));
      return {
        roleId: role.id,
        roleName: role.name,
        assignedMembers: assignments.flatMap((assignment) => {
          const membership = membershipById.get(assignment.membershipId);
          if (!membership) return [];
          const profile = profileByMembershipId.get(membership.id);
          return [{
            membershipId: membership.id,
            fullName: profile?.fullName?.trim() || membership.employeeNo || 'عضو بدون اسم',
            membershipStatus: membership.status,
            isDeleted: Boolean(membership.deletedAt),
            assignmentExpiresAt: assignment.expiresAt,
          }];
        }).sort((left, right) => left.fullName.localeCompare(right.fullName, 'ar')),
        dependentRoles: roles
          .filter((entry) => entry.id !== role.id && (entry.supervisesRoleIds ?? []).includes(role.id))
          .map((entry) => ({ id: entry.id, name: entry.name, isDeleted: Boolean(entry.deletedAt) }))
          .sort((left, right) => left.name.localeCompare(right.name, 'ar')),
      };
    },

    async deleteCompanyRole(input): Promise<void> {
      const companyId = requireCompany(core);
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن حذف دور في منشأة أخرى');
      requireCompanyRoleManager(core, companyId);
      const ctx = core.policy();
      const at = core.now().toISOString();
      const db = await getLocalDb();

      await withTx(db, async (tx) => {
        const rolesStore = storeOf(tx, 'roles');
        const membersStore = storeOf(tx, 'memberships');
        const assignmentsStore = storeOf(tx, 'membership_roles');
        const auditStore = storeOf(tx, 'audit_logs');
        const [roleValue, roleValues, membershipValues, assignmentValues] = await Promise.all([
          rolesStore.get(input.roleId),
          rolesStore.getAll(),
          membersStore.getAll(),
          assignmentsStore.getAll(),
        ]);
        const role = roleValue as Role | undefined;
        if (!role || role.companyId !== companyId || role.deletedAt) throw AppError.notFound('الدور غير موجود');
        const roles = roleValues as Role[];
        const memberships = membershipValues as Membership[];
        const assignments = assignmentValues as MembershipRole[];
        const membershipsById = new Map(memberships.map((membership) => [membership.id, membership]));
        const roleAssignments = assignments.filter((assignment) => assignment.roleId === role.id);
        if (roleAssignments.some((assignment) => membershipsById.get(assignment.membershipId)?.companyId !== companyId)) {
          throw AppError.conflict('تعذّر الحذف لوجود إسناد خارج نطاق المنشأة؛ لم يتم تغيير أي بيانات.');
        }
        const memberIds = [...new Set(roleAssignments.map((assignment) => assignment.membershipId))].sort();
        const dependentRoles = roles.filter(
          (entry) => entry.companyId === companyId && entry.id !== role.id && (entry.supervisesRoleIds ?? []).includes(role.id),
        );
        const dependentRoleIds = dependentRoles.map((entry) => entry.id).sort();
        const expectedMemberIds = [...new Set(input.expectedMemberIds)].sort();
        const expectedDependentRoleIds = [...new Set(input.expectedDependentRoleIds)].sort();
        if (memberIds.join('|') !== expectedMemberIds.join('|') || dependentRoleIds.join('|') !== expectedDependentRoleIds.join('|')) {
          throw AppError.conflict('تغيّرت ارتباطات الدور. أغلق التنبيه وافتح الحذف مجددًا لمراجعة الأسماء قبل الموافقة.');
        }

        for (const assignment of roleAssignments) await assignmentsStore.delete(assignment.id);
        for (const dependent of dependentRoles) {
          await rolesStore.put({
            ...dependent,
            supervisesRoleIds: (dependent.supervisesRoleIds ?? []).filter((id) => id !== role.id),
            updatedAt: at,
          });
        }
        await rolesStore.put({ ...role, deletedAt: at, updatedAt: at });
        await auditStore.put(auditRow(ctx.userId, ctx.membershipId, {
          companyId,
          action: AUDIT_ACTIONS.roleDeleted,
          entityType: 'role',
          entityId: role.id,
          before: { name: role.name, key: role.key, assignedMembershipIds: memberIds, dependentRoleIds },
          after: { deletedAt: at, assignmentsRemoved: memberIds.length, hierarchyReferencesRemoved: dependentRoleIds.length },
        }, at));
      });
      await core.refresh();
    },

    async listCompanyProfiles(input: { companyId: string }) {
      const userId = requireUser(core);
      const activeCompanyId = requireCompany(core);
      if (input.companyId !== activeCompanyId) {
        throw AppError.forbidden('لا يمكن قراءة أعضاء منشأة أخرى');
      }
      const memberships = (await core.rawFind('memberships', 'companyId', activeCompanyId)).filter(
        (membership) => membership.status === 'active',
      );
      if (!memberships.some((membership) => membership.userId === userId)) {
        throw AppError.forbidden('قراءة الأعضاء متاحة لأعضاء المنشأة فقط');
      }
      const profiles = (await core.rawFind('membership_profiles', 'companyId', activeCompanyId)) as Array<{
        membershipId: string; userId: string; fullName: string; phone: string | null; avatarDataUrl: string | null;
      }>;
      return profiles.filter((profile) => memberships.some((membership) => membership.id === profile.membershipId));
    },

    async listCompanyMemberEmails(input) {
      const ctx = core.policy();
      if (!ctx.userId || !ctx.permissions.has('user.view') || input.companyId !== ctx.companyId) {
        throw AppError.forbidden('عرض بريد أعضاء المنشأة يتطلب صلاحية عرض المستخدمين');
      }
      const memberships = (await core.rawFind('memberships', 'companyId', input.companyId)).filter((m) => m.status === 'active');
      const profiles = (await core.rawAll('profiles')) as Profile[];
      return memberships.map((m) => ({ membershipId: m.id, email: profiles.find((p) => p.id === m.userId)?.email ?? '' }));
    },

    async listPlatformMembers() {
      if (!core.policy().isPlatformOwner) throw AppError.forbidden('إدارة أعضاء المنصة لمالك المنصة فقط');
      const members = (await core.rawAll('platform_members')) as Array<{ userId: string; roleName?: string; status: 'active' | 'suspended' }>;
      const profiles = (await core.rawAll('profiles')) as Profile[];
      const allPermissions = (await core.rawAll('platform_member_permissions')) as Array<{ userId: string; permissionKey: PermissionKey }>;
      return members.map((m) => {
        const profile = profiles.find((p) => p.id === m.userId);
        return { userId: m.userId, email: profile?.email ?? '', fullName: profile?.fullName ?? '', roleName: m.roleName ?? 'مخصص', status: m.status,
          permissions: allPermissions.filter((p) => p.userId === m.userId).map((p) => p.permissionKey) };
      });
    },

    async savePlatformMember(input) {
      if (!core.policy().isPlatformOwner) throw AppError.forbidden('إدارة أعضاء المنصة لمالك المنصة فقط');
      const email = input.email.trim().toLowerCase();
      const profiles = (await core.rawAll('profiles')) as Profile[];
      const profile = profiles.find((p) => p.email.trim().toLowerCase() === email);
      if (!profile) throw AppError.notFound('لا يوجد حساب بهذا البريد؛ يجب أن يسجل المستخدم حسابًا أولًا');
      const allowed = new Set(DELEGABLE_PLATFORM_PERMISSIONS);
      const permissions = [...new Set(input.permissions)].filter((p) => allowed.has(p));
      const now = core.now().toISOString();
      await core.rawPut('platform_members', { id: profile.id, userId: profile.id, status: 'active', roleName: input.roleName ?? 'مخصص', createdBy: core.policy().userId, createdAt: now, updatedAt: now });
      for (const row of await core.rawFind('platform_member_permissions', 'userId', profile.id)) { const db = await getLocalDb(); await db.delete('platform_member_permissions', row.id); }
      for (const permissionKey of permissions) await core.rawPut('platform_member_permissions', { id: newId(), userId: profile.id, permissionKey });
      await core.audit({ companyId: null, action: 'platform.member_permissions_updated', entityType: 'platform_member', entityId: profile.id, after: { permissions } });
    },

    async removePlatformMember(input) {
      if (!core.policy().isPlatformOwner) throw AppError.forbidden('إدارة أعضاء المنصة لمالك المنصة فقط');
      if (input.userId === core.policy().userId) throw AppError.validation('لا يمكنك إزالة صلاحياتك بنفسك');
      { const db = await getLocalDb(); await db.delete('platform_members', input.userId); }
      for (const row of await core.rawFind('platform_member_permissions', 'userId', input.userId)) { const db = await getLocalDb(); await db.delete('platform_member_permissions', row.id); }
      await core.audit({ companyId: null, action: 'platform.member_removed', entityType: 'platform_member', entityId: input.userId });
    },

    async bootstrapPlatformOwner(input: BootstrapOwnerInput): Promise<SignInResult> {
      const owners = await core.rawAll('platform_owners');
      if (owners.some((o) => !o.revokedAt)) {
        throw AppError.conflict('تمت تهيئة مالك المنصة مسبقًا');
      }
      await verifyBootstrapToken(input.bootstrapToken);

      const db = await getLocalDb();
      const email = input.email.trim().toLowerCase();
      const existing = (await db.getFromIndex('local_accounts', 'email', email)) as
        | { id: string; passwordHash: string; salt: string; iterations: number }
        | undefined;

      let userId: string;
      if (existing) {
        const valid = await verifySecret(input.password, {
          hash: existing.passwordHash,
          salt: existing.salt,
          iterations: existing.iterations,
        });
        if (!valid) {
          throw AppError.unauthenticated('كلمة المرور لا تطابق الحساب الموجود بهذا البريد');
        }
        userId = existing.id;
      } else {
        const account = await createLocalAccount({
          email,
          password: input.password,
          fullName: input.fullName,
        });
        userId = account.id;
      }

      const now = core.now().toISOString();
      const owner: PlatformOwner = {
        id: userId,
        userId,
        grantedBy: null,
        grantedAt: now,
        revokedAt: null,
        note: 'تهيئة أولية',
      };
      await core.rawPut('platform_owners', owner);

      const profile = await core.rawGet('profiles', userId);
      if (!profile) {
        const newProfile: Profile = {
          id: userId,
          email,
          fullName: input.fullName.trim(),
          phone: null,
          avatarAttachmentId: null,
          avatarDataUrl: null,
          locale: 'ar',
          status: 'active',
          lastSeenAt: now,
          createdAt: now,
          updatedAt: now,
        };
        await core.rawPut('profiles', newProfile);
      }

      await core.audit({
        companyId: null,
        action: AUDIT_ACTIONS.platformBootstrapped,
        entityType: 'platform_owner',
        entityId: userId,
        after: { note: 'تعيين مالك المنصة الأول' },
      });

      await core.auth.signIn(email, input.password);
      await core.refresh();
      return { userId, email, requiresEmailConfirmation: false };
    },

    async cancelTemplateOccurrences(input): Promise<number> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requirePermission(core, 'task.template.manage');
      const template = await core.rawGet('task_templates', input.templateId);
      if (!template || template.companyId !== companyId) throw AppError.notFound('القالب غير موجود');

      const tasks = await core.rawFind('tasks', 'templateId', template.id);
      const cancellable = tasks.filter(
        (t) =>
          !t.deletedAt &&
          t.localDate >= input.fromDate &&
          t.status !== 'completed' &&
          t.status !== 'cancelled',
      );
      const at = core.now().toISOString();
      const db = await getLocalDb();
      await withTx(db, async (tx) => {
        for (const task of cancellable) {
          await storeOf(tx, 'tasks').put({
            ...task,
            status: 'cancelled',
            cancelledAt: at,
            cancelledByMembershipId: input.actorMembershipId,
            version: task.version + 1,
            updatedAt: at,
          } satisfies Task);
          await storeOf(tx, 'task_status_history').put({
            id: newId(),
            taskId: task.id,
            fromStatus: task.status,
            toStatus: 'cancelled',
            fromApproval: task.approvalState,
            toApproval: task.approvalState,
            actorMembershipId: input.actorMembershipId,
            actorUserId: userId,
            reason: input.reason,
            createdAt: at,
          } satisfies TaskStatusHistory);
        }
        await storeOf(tx, 'audit_logs').put(
          auditRow(
            userId,
            input.actorMembershipId,
            {
              companyId,
              action: AUDIT_ACTIONS.taskTemplateOccurrencesCancelled,
              entityType: 'task_template',
              entityId: template.id,
              after: { count: cancellable.length, fromDate: input.fromDate },
            },
            at,
          ),
        );
      });
      return cancellable.length;
    },

    async applyRetention(input: ApplyRetentionInput) {
      const userId = requireUser(core);
      requirePermission(core, 'attachment.retention.manage');
      if (input.companyId !== core.policy().companyId) {
        throw AppError.forbidden('لا يمكن تنفيذ السياسة على منشأة أخرى');
      }
      const settings = (await core.rawAll('settings')) as SettingRecord[];
      const chain = [
        { scopeType: 'platform' as const, scopeId: 'global' },
        { scopeType: 'company' as const, scopeId: input.companyId },
      ];
      const policy = resolveSetting<RetentionPolicy>(
        settings,
        chain,
        SETTING_KEYS.retentionPolicy,
        DEFAULT_RETENTION_POLICY,
      );

      const attachments = (await core.rawFind('attachments', 'companyId', input.companyId)).filter(
        (a) => a.uploadState === 'uploaded' && !a.deletedAt,
      );
      const eligible = attachments.filter((a) => {
        if (policy.mode !== 'by_age' || !policy.days) return false;
        if (policy.imagesOnly && a.kind !== 'image') return false;
        const ageMs = core.now().getTime() - new Date(a.createdAt).getTime();
        return ageMs >= policy.days * 86_400_000;
      });
      const bytesFreed = eligible.reduce((sum, a) => sum + a.sizeBytes, 0);

      if (input.dryRun) {
        return {
          filesCount: eligible.length,
          bytesFreed,
          attachmentIds: eligible.map((a) => a.id),
          deletionId: null,
        };
      }

      const deletionId = newId();
      const startedAt = core.now().toISOString();
      await core.rawPut('attachment_deletions', {
        id: deletionId,
        companyId: input.companyId,
        mode: 'retention_policy',
        requestedByUserId: userId,
        scope: input.scope ?? {},
        filesCount: 0,
        bytesFreed: 0,
        status: 'in_progress',
        errors: [],
        startedAt,
        finishedAt: null,
        createdAt: startedAt,
      });

      const errors: Array<{ attachmentId: string; message: string }> = [];
      let freed = 0;
      for (const attachment of eligible) {
        try {
          await core.blob.remove(attachment.storagePath);
          await core.rawPut('attachments', {
            ...attachment,
            uploadState: 'deleted',
            deletedAt: core.now().toISOString(),
            deletionId,
            updatedAt: core.now().toISOString(),
          });
          freed += attachment.sizeBytes;
        } catch (error) {
          errors.push({
            attachmentId: attachment.id,
            message: error instanceof Error ? error.message : 'فشل الحذف',
          });
        }
      }

      const finishedAt = core.now().toISOString();
      await core.rawPut('attachment_deletions', {
        id: deletionId,
        companyId: input.companyId,
        mode: 'retention_policy',
        requestedByUserId: userId,
        scope: input.scope ?? {},
        filesCount: eligible.length - errors.length,
        bytesFreed: freed,
        status: errors.length > 0 ? 'failed' : 'completed',
        errors,
        startedAt,
        finishedAt,
        createdAt: startedAt,
      });

      await core.audit({
        companyId: input.companyId,
        action: AUDIT_ACTIONS.retentionSweepExecuted,
        entityType: 'attachment_deletion',
        entityId: deletionId,
        after: {
          filesCount: eligible.length - errors.length,
          bytesFreed: freed,
          errors: errors.length,
        },
      });

      return {
        filesCount: eligible.length - errors.length,
        bytesFreed: freed,
        attachmentIds: eligible.map((a) => a.id),
        deletionId,
      };
    },

    async uploadAttachment(input: UploadAttachmentInput): Promise<Attachment> {
      const userId = requireUser(core);
      requirePermission(core, 'attachment.upload');
      const companyId = requireCompany(core);
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن الرفع لمنشأة أخرى');

      const settings = (await core.rawAll('settings')) as SettingRecord[];
      const chain = [
        { scopeType: 'platform' as const, scopeId: 'global' },
        { scopeType: 'company' as const, scopeId: companyId },
      ];
      const uploadPolicy = resolveSetting<UploadPolicy>(
        settings,
        chain,
        SETTING_KEYS.uploadPolicy,
        DEFAULT_UPLOAD_POLICY,
      );
      if (!uploadPolicy.allowedMimeTypes.includes(input.mimeType)) {
        throw AppError.validation('نوع الملف غير مسموح');
      }
      if (input.bytes.byteLength > uploadPolicy.maxSizeMb * 1024 * 1024) {
        throw AppError.validation(`حجم الملف يتجاوز الحد (${uploadPolicy.maxSizeMb} ميجابايت)`);
      }

      const existing = (await core.rawFind('attachments', 'clientRef', input.clientRef)).find(
        (a) => a.uploadedByUserId === userId && !a.deletedAt,
      );
      if (existing) {
        return (await core.table('attachments').get(existing.id)) ?? existing;
      }

      if (input.taskId) {
        const task = await loadTask(core, input.taskId);
        const subject = await subjectFor(core, task);
        if (!canCompleteTask({ ctx: core.policy().access, subject }).allowed) {
          throw AppError.forbidden('لا يمكنك رفع مرفق لمهمة لست مسؤولًا عنها');
        }
      }

      const retention = resolveSetting<RetentionPolicy>(
        settings,
        chain,
        SETTING_KEYS.retentionPolicy,
        DEFAULT_RETENTION_POLICY,
      );
      const attachmentId = newId();
      const storagePath = buildStoragePath({
        companyId,
        taskId: input.taskId,
        attachmentId,
        fileName: input.fileName,
      });
      const checksum = await sha256Hex(input.bytes);
      const now = core.now();
      const attachment: Attachment = {
        id: attachmentId,
        companyId,
        taskId: input.taskId,
        taskAssigneeId: input.taskAssigneeId,
        storagePath,
        fileName: input.fileName,
        mimeType: input.mimeType,
        sizeBytes: input.bytes.byteLength,
        kind: input.kind,
        uploadState: 'uploaded',
        uploadedByUserId: userId,
        checksum,
        clientRef: input.clientRef,
        retentionUntil: retentionExpiry(retention, now),
        deletedAt: null,
        deletionId: null,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      };

      await core.blob.put(storagePath, input.bytes, input.mimeType);
      try {
        return await core.table('attachments').insert(attachment);
      } catch (error) {
        await core.blob.remove(storagePath);
        throw error;
      }
    },

    async deleteAttachment(input): Promise<void> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requirePermission(core, 'attachment.delete');
      const attachment = await core.rawGet('attachments', input.attachmentId);
      if (!attachment || attachment.companyId !== companyId) {
        throw AppError.notFound('المرفق غير موجود');
      }
      const at = core.now().toISOString();
      await core.blob.remove(attachment.storagePath);
      await core.table('attachments').update(attachment.id, {
        uploadState: 'deleted',
        deletedAt: at,
        updatedAt: at,
      });
      await core.audit({
        companyId,
        action: AUDIT_ACTIONS.attachmentDeleted,
        entityType: 'attachment',
        entityId: attachment.id,
        before: { fileName: attachment.fileName, sizeBytes: attachment.sizeBytes },
        after: { mode: input.mode, by: userId },
      });
    },

    async createAttachmentUrl(input): Promise<string> {
      requireUser(core);
      const attachment = await core.table('attachments').get(input.attachmentId);
      if (!attachment || attachment.deletedAt) throw AppError.notFound('المرفق غير موجود');
      if (attachment.uploadState !== 'uploaded') throw AppError.notFound('الملف غير متاح');
      return core.blob.createUrl(attachment.storagePath, input.expiresInSeconds);
    },

    async markNotificationRead(input): Promise<void> {
      const userId = requireUser(core);
      if (input.actorUserId !== userId) throw AppError.forbidden('لا يمكن تعديل إشعار مستخدم آخر');
      const notification = await core.rawGet('notifications', input.notificationId);
      if (!notification || notification.recipientUserId !== userId) {
        throw AppError.notFound('الإشعار غير موجود');
      }
      if (notification.readAt) return;
      const at = core.now().toISOString();
      await core.rawPut('notifications', { ...notification, readAt: at });
      await core.audit({
        companyId: notification.companyId,
        action: AUDIT_ACTIONS.notificationRead,
        entityType: 'notification',
        entityId: notification.id,
        after: { readAt: at },
      });
    },

    async markAllNotificationsRead(input): Promise<number> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      if (input.actorUserId !== userId) throw AppError.forbidden('لا يمكن تعديل إشعارات مستخدم آخر');
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن تعديل إشعارات منشأة أخرى');
      const notifications = (await core.rawFind('notifications', 'recipientUserId', userId)).filter(
        (n) => n.companyId === companyId && !n.readAt,
      );
      const at = core.now().toISOString();
      for (const notification of notifications) {
        await core.rawPut('notifications', { ...notification, readAt: at });
      }
      if (notifications.length > 0) {
        await core.audit({
          companyId,
          action: AUDIT_ACTIONS.notificationRead,
          entityType: 'notification',
          entityId: null,
          after: { count: notifications.length },
        });
      }
      return notifications.length;
    },

    async sweepMyNotifications(input): Promise<number> {
      const userId = requireUser(core);
      const companyId = requireCompany(core);
      requirePermission(core, 'task.view');
      if (input.actorUserId !== userId) throw AppError.forbidden('لا يمكن توليد تنبيهات لمستخدم آخر');
      if (input.companyId !== companyId) throw AppError.forbidden('لا يمكن توليد تنبيهات لشركة أخرى');
      const membershipId = core.policy().membershipId;
      if (!membershipId) return 0;

      const now = core.now();
      const horizon = now.getTime() + Math.max(1, input.withinHours) * 3_600_000;
      const tasks = (await core.rawFind('tasks', 'companyId', companyId)).filter(
        (task) =>
          !task.deletedAt &&
          task.dueAt !== null &&
          (task.status === 'scheduled' || task.status === 'pending' || task.status === 'in_progress'),
      );
      const existing = await core.rawFind('notifications', 'recipientUserId', userId);
      let created = 0;

      for (const task of tasks) {
        const due = task.dueAt ? new Date(task.dueAt).getTime() : Number.NaN;
        if (Number.isNaN(due)) continue;
        const assignees = await core.rawFind('task_assignees', 'taskId', task.id);
        const involved =
          assignees.some((assignee) => assignee.membershipId === membershipId && !assignee.unassignedAt) ||
          task.approverMembershipId === membershipId ||
          task.createdByMembershipId === membershipId;
        if (!involved) continue;

        let type: NotificationType | null = null;
        if (due < now.getTime()) type = 'task.overdue';
        else if (due <= horizon) type = 'task.due_soon';
        if (!type) continue;
        if (existing.some((notification) => notification.taskId === task.id && notification.type === type)) {
          continue;
        }

        const row = notificationRow({
          companyId,
          recipientUserId: userId,
          type,
          title: type === 'task.overdue' ? 'مهمة متأخرة' : 'مهمة تقترب من موعدها',
          body: task.title,
          taskId: task.id,
          now: now.toISOString(),
        });
        await core.rawPut('notifications', row);
        existing.push(row as never);
        created += 1;
      }
      return created;
    },
  };
}

/* ================================ مساعدات ================================ */

/** المعتمد الافتراضي: المشرف المباشر لمنشئ المهمة الذي يملك حق الاعتماد. */
async function resolveDefaultApprover(core: LocalCore, task: Task): Promise<string | null> {
  const relations = await core.rawFind(
    'supervision_relations',
    'subordinateMembershipId',
    task.createdByMembershipId,
  );
  const active = relations.find((r) => !r.endsAt && r.canApproveTasks);
  return active?.supervisorMembershipId ?? null;
}

async function resolveTemplateAssignees(core: LocalCore, template: TaskTemplate): Promise<string[]> {
  const memberships = (await core.rawFind('memberships', 'companyId', template.companyId)).filter(
    (m) => m.status === 'active',
  );
  const branches = await core.rawFind('branches', 'companyId', template.companyId);
  const explicit = await core.rawFind('task_template_assignees', 'templateId', template.id);
  const scope = template.assigneeScope;

  const ids = new Set<string>();
  for (const id of scope.membershipIds) ids.add(id);
  for (const assignment of explicit) {
    if (assignment.membershipId) ids.add(assignment.membershipId);
  }

  const branchIds = new Set<string>(scope.branchIds);
  if (template.branchId) branchIds.add(template.branchId);
  if (branchIds.size > 0) {
    for (const membership of memberships) {
      if (membership.primaryBranchId && branchIds.has(membership.primaryBranchId)) {
        ids.add(membership.id);
      }
    }
  }

  if (scope.brandIds.length > 0) {
    const brandBranchIds = new Set(
      branches.filter((b) => b.brandId && scope.brandIds.includes(b.brandId)).map((b) => b.id),
    );
    for (const membership of memberships) {
      if (membership.primaryBranchId && brandBranchIds.has(membership.primaryBranchId)) {
        ids.add(membership.id);
      }
    }
  }

  if (scope.includeSupervised) {
    for (const supervised of core.policy().access.membership?.supervisedMembershipIds ?? []) {
      ids.add(supervised);
    }
  }

  const valid = new Set(memberships.map((m) => m.id));
  return [...ids].filter((id) => valid.has(id));
}

/* ------------------------- تهيئة رمز التهيئة المحلي ------------------------ */

const BOOTSTRAP_META_KEY = 'bootstrap.token';

export async function setLocalBootstrapToken(token: string): Promise<void> {
  if (token.trim().length < 16) {
    throw AppError.validation('رمز التهيئة يجب أن يكون 16 حرفًا على الأقل');
  }
  const db = await getLocalDb();
  const existing = await db.get('local_meta', BOOTSTRAP_META_KEY);
  if (existing) throw AppError.conflict('رمز التهيئة مُعرَّف مسبقًا على هذا الجهاز');
  const secret = await hashSecret(token.trim());
  await db.put('local_meta', {
    key: BOOTSTRAP_META_KEY,
    value: secret,
    updatedAt: new Date().toISOString(),
  });
}

export async function hasLocalBootstrapToken(): Promise<boolean> {
  const db = await getLocalDb();
  return (await db.get('local_meta', BOOTSTRAP_META_KEY)) !== undefined;
}

const BOOTSTRAP_LINK_KEY = 'taskflow.bootstrap.link.visible';

/** في الوضع المحلي فقط: تحكم مالك المنصة في رابط التهيئة (افتراضيًا ظاهر). */
export function getLocalBootstrapLinkVisibility(): boolean {
  try {
    return localStorage.getItem(BOOTSTRAP_LINK_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setLocalBootstrapLinkVisibility(visible: boolean): void {
  try {
    localStorage.setItem(BOOTSTRAP_LINK_KEY, String(visible));
  } catch {
    // التخزين المحلي قد يكون معطّلًا؛ لا نُفشل العملية.
  }
}

const PLATFORM_BRANDING_KEY = 'taskflow.platform.branding';
const PLATFORM_THEME_KEY = 'taskflow.platform.theme';

/** هوية المنصة في الوضع المحلي (نصوص صفحة الدخول والسمة). */
export function getLocalPlatformBranding(): PlatformBranding {
  try {
    const raw = localStorage.getItem(PLATFORM_BRANDING_KEY);
    if (!raw) return { ...DEFAULT_PLATFORM_BRANDING };
    const parsed = JSON.parse(raw) as Partial<PlatformBranding>;
    const result = { ...DEFAULT_PLATFORM_BRANDING };
    for (const key of Object.keys(DEFAULT_PLATFORM_BRANDING) as Array<keyof PlatformBranding>) {
      const value = parsed[key];
      if (typeof value === 'string' && value.trim().length > 0) result[key] = value;
    }
    return result;
  } catch {
    return { ...DEFAULT_PLATFORM_BRANDING };
  }
}

export function getLocalPlatformTheme(): ThemeTokens | null {
  try {
    const raw = localStorage.getItem(PLATFORM_THEME_KEY);
    return raw ? (JSON.parse(raw) as ThemeTokens) : null;
  } catch {
    return null;
  }
}

export function setLocalPlatformBranding(
  branding: PlatformBranding,
  theme: ThemeTokens | null,
): void {
  try {
    localStorage.setItem(PLATFORM_BRANDING_KEY, JSON.stringify(branding));
    if (theme) localStorage.setItem(PLATFORM_THEME_KEY, JSON.stringify(theme));
    else localStorage.removeItem(PLATFORM_THEME_KEY);
  } catch {
    // التخزين المحلي قد يكون معطّلًا؛ لا نُفشل العملية.
  }
}

async function verifyBootstrapToken(token: string): Promise<void> {
  const db = await getLocalDb();
  const stored = await db.get('local_meta', BOOTSTRAP_META_KEY);
  if (!stored) {
    throw AppError.validation(
      'لم يُعرَّف رمز تهيئة على هذا الجهاز. أنشئ رمز التهيئة أولًا من الخطوة الأولى.',
    );
  }
  const value = stored.value as { hash: string; salt: string; iterations: number };
  const valid = await verifySecret(token.trim(), value);
  if (!valid) throw AppError.forbidden('رمز التهيئة غير صحيح');
}
